/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// ─── CalendlyFormHandler ──────────────────────────────────────────────────────

class CalendlyFormHandler {
  constructor() {
    this.forms = [...document.querySelectorAll("[calendly-form-type]")];
    this.url = this._getUrls();
  }

  _init() {
    this.forms.forEach((form) => {
      form.addEventListener("submit", (e) => {
        this._showApptForm(e, form);
      });
    });
  }

  _createColorParam() {
    const colors = {
      background_color: "fdfcf8",
      text_color: "313131",
      primary_color: "d1741b",
    };

    return Object.entries(colors)
      .map(([key, value]) => `${key}=${value}`)
      .join("&");
  }

  _showApptForm(event, form) {
    event.preventDefault();
    const formType = form.getAttribute("calendly-form-type");
    const apptType = form.querySelector("[name='appt-type']").value;
    const apptTime = form.querySelector("[name='appt-time']").value;
    const formUrl = this.url[formType]?.[apptType]?.[apptTime] || null;

    if (formUrl) {
      Calendly.initPopupWidget({
        url: `${formUrl}?${this._createColorParam()}`,
        resize: true,
      });
    } else {
      alert("Please select a valid meeting type and duration.");
    }
  }

  _getUrls() {
    const base = "https://calendly.com/d";

    const data = {
      collections: {
        slug: "srj-collections-consultation",
        "in-person": { 30: "crhk-8wk-7fk", 60: "cmzn-w9r-th3" },
        virtual: { 30: "cnr6-3fr-t66", 60: "cm2k-yct-z76" },
      },
      bridal: {
        slug: "bridal-engagement-consultation",
        "in-person": { 30: "cm2m-jcz-q7v", 60: "cnk6-p3g-x3s" },
        virtual: { 30: "cms5-6hf-866", 60: "cqr7-85q-6ry" },
      },
      "bespoke-bridal": {
        slug: "bespoke-bridal-consultation",
        "in-person": { 30: "ct3n-kq4-twr", 60: "ctxd-xhq-y54" },
        virtual: { 30: "cyh9-8z2-tj6", 60: "cxsy-fxn-v5b" },
      },
      custom: {
        slug: "custom-design-consultation",
        "in-person": { 30: "crpp-9h8-txv", 60: "crpc-tcm-xcf" },
        virtual: { 30: "cqqx-2cb-ykx", 60: "cqr9-2pp-c4z" },
      },
    };

    const urls = {};
    for (const [apptType, { slug, ...modes }] of Object.entries(data)) {
      for (const [mode, durations] of Object.entries(modes)) {
        urls[mode] ??= {};
        urls[mode][apptType] = {};
        for (const duration of [30, 60]) {
          urls[mode][apptType][duration] =
            `${base}/${durations[duration]}/${slug}-${mode}`;
        }
      }
    }

    return urls;
  }
}

// ─── ThankYouPageHandler ──────────────────────────────────────────────────────

class ThankYouPageHandler {
  constructor() {
    this.eventName =
      this.getQueryParam("event_type_name") || "your appointment";
    this.eventDate = this.getQueryParam("event_start_time");
    this.inviteeName = this.getQueryParam("invitee_first_name") || "Guest";
    this.formattedDate = this.eventDate
      ? new Date(this.eventDate).toLocaleString()
      : "";
  }

  getQueryParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  displayMessage() {
    document.getElementById("thank-you-message").innerText =
      `Thanks for booking, ${this.inviteeName}! Your ${this.eventName} is scheduled for ${this.formattedDate}.`;
  }

  init() {
    this.displayMessage();
  }
}

// ─── customGalImgHandler ──────────────────────────────────────────────────────

const customGalImgHandler = {
  init() {
    // Query fresh each time in case section re-renders
    const imgWrappers = [
      ...document.querySelectorAll(".custom_gallery--image_wrapper"),
    ];
    imgWrappers.forEach((wrapper) => {
      const primaryImg = wrapper.querySelector(
        ".custom_gallery--primary_image",
      );
      const secondaryImg = wrapper.querySelector(
        ".custom_gallery--secondary_image",
      );

      const toggleImages = () => {
        primaryImg.classList.toggle("hidden");
        secondaryImg.classList.toggle("hidden");
      };

      if (secondaryImg) {
        wrapper.addEventListener("mouseover", toggleImages);
        wrapper.addEventListener("mouseout", toggleImages);
      }
    });
  },
};

// ─── dynamicPreOrderDate ──────────────────────────────────────────────────────

const dynamicPreOrderDate = {
  calcShipDate(date, numDays) {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + numDays);
    return newDate;
  },
  init() {
    // Query fresh each time in case section re-renders
    const preOrderWrapper = document.querySelector(".pre-order--message");
    if (!preOrderWrapper) return;

    const timeline = Number(preOrderWrapper.dataset.timeline) || 7;
    const shipDate = this.calcShipDate(new Date(), timeline);
    const dateWrapper = preOrderWrapper.querySelector("#dynamic-date");
    if (!dateWrapper) return;

    dateWrapper.textContent = shipDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  },
};

class AccordionHandler {
  constructor() {
    const accordionSections = {
      faq: [...document.querySelectorAll(".faq__item")],
      filter: [...document.querySelectorAll(".product-facet__filter-item")],
      mobileNav: [
        ...document.querySelectorAll('.mobile-nav__item[data-level="1"]'),
      ],
      mobileNavChildren: [
        ...document.querySelectorAll('.mobile-nav__item[data-level="2"]'),
      ],
    };

    for (const [key, value] of Object.entries(accordionSections)) {
      if (value.length) this._init(value);
    }
  }

  _init(arr) {
    arr.forEach((item) => {
      const toggleButton = item.querySelector("button");
      toggleButton?.addEventListener("click", (e) => {
        this._closeOtherAccordions(arr, item);
      });
    });
  }

  _closeOtherAccordions(parentArr, clickedItem) {
    parentArr.forEach((item) => {
      if (item !== clickedItem) {
        const toggleButton = item.querySelector("button");
        const toggleContent = item.querySelector("collapsible-content");

        toggleButton?.setAttribute("aria-expanded", "false");
        toggleContent?.removeAttribute("open");
      }
    });
  }
}

// ─── Bridal ─────────────────────────────────────────────────────────────

class Bridal {
  constructor() {
    this._init();
  }

  _init() {
    if (!document.body.classList.contains("tab--bridal-custom")) return;

    this._createLenis();
    this._renderLenis();
  }

  _createLenis() {
    this.lenis = new Lenis({
      lerp: 0.05,
      easing: (t) => 1 - Math.pow(1 - t, 5),
    });
  }

  _renderLenis(time) {
    this.lenis.raf(time);
    requestAnimationFrame(this._renderLenis.bind(this));
  }
}

// ─── VariantViews ─────────────────────────────────────────────────────────────

class VariantViews {
  constructor() {
    this.mainImg = document.getElementById("product-views--main-image");
    this.miniImg = document.getElementById("product-views--mini-image");
    this.slider = document.querySelector(".view-buttons__slider");
    this.container = document.querySelector(".view-buttons");
    this._buttons = null;

    if (!this.mainImg) return;

    this.init();
  }

  get buttons() {
    return (this._buttons ??= [...document.querySelectorAll(".view-btn")]);
  }

  init() {
    const watch = (mq, key) => {
      this[key] = mq.matches;
      mq.addEventListener("change", (e) => {
        this[key] = e.matches;
        this.onBreakpointChange();
      });
    };

    watch(window.matchMedia("(hover: none)"), "isTouch");
    watch(window.matchMedia("(max-width:740px)"), "isMobile");

    this.onBreakpointChange();
    this.preloadAllVariantImages();
    this.observeMiniImage();

    this.container.addEventListener("click", (e) => {
      const btn = e.target.closest(".view-btn");
      if (btn && !btn.disabled) this.onButtonClick(btn);
    });

    document.addEventListener("variant:changed", (e) =>
      this.onVariantChanged(e),
    );

    const resizeObserver = new ResizeObserver(() => {
      if (!this.isTouch)
        this.moveSlider(this.container.querySelector(".view-btn.active"));
    });
    resizeObserver.observe(this.container);
  }

  onBreakpointChange() {
    this.slider.style.display = this.isTouch ? "none" : "";
    if (!this.isTouch)
      this.moveSlider(this.container.querySelector(".view-btn.active"));
    if (this.miniImg && !this.isMobile)
      this.miniImg.classList.remove("is-visible");
  }

  observeMiniImage() {
    if (!this.miniImg) return;

    let mainImgOut = false;
    let sentinelOut = true;

    const update = () => {
      this.miniImg.classList.toggle(
        "is-visible",
        mainImgOut && sentinelOut && this.isMobile,
      );
    };

    new IntersectionObserver(
      ([entry]) => {
        mainImgOut = !entry.isIntersecting;
        update();
      },
      { threshold: 0 },
    ).observe(this.mainImg);

    const observeSentinel = () => {
      const sentinel = document.querySelector(".product-form__add-button");
      if (!sentinel) {
        setTimeout(observeSentinel, 300);
        return;
      }

      new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            sentinelOut = false;
          } else if (entry.boundingClientRect.top > 0) {
            sentinelOut = true;
          }
          update();
        },
        { threshold: 0, rootMargin: "0px 0px -80% 0px" },
      ).observe(sentinel);
    };

    observeSentinel();

    this.miniImg.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  }

  preloadAllVariantImages() {
    if (!window.variantViews) return;
    const allSrcs = Object.values(window.variantViews)
      .flatMap((views) => Object.values(views))
      .filter(Boolean);

    const unique = [...new Set(allSrcs)];
    const load = (i) => {
      if (i >= unique.length) return;
      const img = new Image();
      img.onload = img.onerror = () => load(i + 1);
      img.src = unique[i];
    };

    setTimeout(() => load(0), 500);
  }

  setImage(src) {
    if (!src || src === this.mainImg.src) return;

    this.mainImg.classList.add("is-loading");

    const preload = new Image();
    preload.onload = () => {
      this.mainImg.src = src;
      if (this.miniImg) this.miniImg.src = src;
      this.mainImg.classList.remove("is-loading");
    };
    preload.onerror = () => {
      this.mainImg.classList.remove("is-loading");
    };
    preload.src = src;
  }

  moveSlider(btn) {
    if (!btn) return;
    requestAnimationFrame(() => {
      const containerRect = this.container.getBoundingClientRect();
      const btnRect = btn.getBoundingClientRect();
      const left = btnRect.left - containerRect.left - 4;

      this.slider.style.width = `${btnRect.width}px`;
      this.slider.style.height = `${btnRect.height}px`;
      this.slider.style.transform = `translateX(${left}px)`;
      this.slider.style.top = `${(this.container.offsetHeight - btnRect.height) / 2}px`;
    });
  }

  setActive(btn) {
    if (!btn) return;
    this.buttons.forEach((b) => b.classList.remove("active"));
    btn.classList.add("active");
    if (!this.isTouch) this.moveSlider(btn);
  }

  onButtonClick(btn) {
    if (btn.classList.contains("active") || btn.disabled) return;
    this.setImage(btn.dataset.src);
    this.setActive(btn);
  }

  onVariantChanged(e) {
    const views = window.variantViews[e.detail.variant.id];
    if (!views) return;

    const keys = ["front", "side", "quarter", "model"];

    this.buttons.forEach((btn, i) => {
      const src = views[keys[i]];
      btn.dataset.src = src || "";
      btn.disabled = !src;
    });

    const firstEnabled = this.buttons.find((b) => !b.disabled);
    if (!firstEnabled) return;

    this.setImage(views.front || firstEnabled.dataset.src);
    this.setActive(firstEnabled);
  }
}

// ─── Ring Sizer ──────────────────────────────────────────────────────────────
class RingSizer {
  constructor() {
    this.overlay = document.getElementById("ringSizerOverlay");
    this.hand = document.getElementById("ringSizerHand");
    this.triggerBtn = document.getElementById("ringSizerOpen");
    this.closeBtn = document.getElementById("ringSizerClose");
    this.caratClasses = ["carat-2", "carat-2-5", "carat-3"];
    this.shapeText = document.querySelector(
      ".ring-sizer__shape-wrapper .heading--small > span",
    );

    if (!this.overlay || !this.hand || !this.triggerBtn || !this.closeBtn)
      return;

    this.init();
  }

  init() {
    this.initModal();
    this.initShapes();
    this.initCarats();
  }

  initModal() {
    this.triggerBtn.addEventListener("click", () => this.open());
    this.closeBtn.addEventListener("click", () => this.close());
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });
  }

  initShapes() {
    document.querySelectorAll(".ring-sizer__shape-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.swapActive(".ring-sizer__shape-btn", btn);
        this.swapActive(
          ".ring-sizer__stone-img",
          this.hand.querySelector(
            `.ring-sizer__stone-img.${btn.dataset.shape}`,
          ),
        );

        if (this.shapeText) {
          this.shapeText.textContent = btn.getAttribute("aria-label");
        }
      });
    });

    // Set initial shape name on load
    const firstActive = document.querySelector(".ring-sizer__shape-btn.active");
    if (firstActive && this.shapeText) {
      this.shapeText.textContent = firstActive.getAttribute("aria-label");
    }
  }

  initCarats() {
    document.querySelectorAll(".ring-sizer__carat-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        this.swapActive(".ring-sizer__carat-btn", btn);
        this.hand.classList.remove(...this.caratClasses);
        this.hand.classList.add(btn.dataset.carat);
      });
    });
  }

  open() {
    this.overlay.classList.add("is-open");
    document.body.style.overflow = "hidden";
  }

  close() {
    this.overlay.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  swapActive(selector, next) {
    document
      .querySelectorAll(selector)
      .forEach((el) => el.classList.remove("active"));
    next.classList.add("active");
  }
}

//

// ─── a11y ─────────────────────────────────────────────────────────────────────
function keyToClick(event) {
  if (event.key == "Enter") {
    event.preventDefault();
    event.target.click();
  }
}

// ─── Init ─────────────────────────────────────────────────────────────────────

function initPage() {
  const apptConfirmPath = "/pages/appointment-confirmation";

  if (window.location.pathname.includes(apptConfirmPath)) {
    const thankYouHandler = new ThankYouPageHandler();
    thankYouHandler.init();
  }

  const calendly = new CalendlyFormHandler();
  calendly._init();

  new Bridal();
  new VariantViews();
  new RingSizer();
  new AccordionHandler();

  customGalImgHandler.init();
  dynamicPreOrderDate.init();
}

document.addEventListener("DOMContentLoaded", initPage);
document.addEventListener("shopify:section:load", initPage);
